import { useEffect, useState } from 'react';

import { useExcelImporterStepperContext } from '@/views/AdminDashboard/components/ExcelImporterStepper/context';

import type { TErrorCodesMatrix } from './types';

type TValidations = (boolean | undefined)[];

const useDataValidation = (errorCodesMatrix: TErrorCodesMatrix) => {
  const {
    file,
    fileNameUrl,
    filePathUrl,
    setSnackbar,
    handleOnChangeFile,
    onValidate,
    onSubmit,
    handleNextStep,
    continueOnError,
    centralized,
    onValidated,
    validationMetadata,
    validationStepVisited,
    setVisitedValidationStep,
    isNewUpload,
  } = useExcelImporterStepperContext();
  const [validations, setValidations] = useState<TValidations>(
    Array(Object.keys(errorCodesMatrix).length).fill(undefined),
  );
  const [isSuccessBar, setIsSuccessBar] = useState(true);
  const [percent, setPercent] = useState(0);

  const delay = !validationStepVisited ? 1000 : 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (file && !isNewUpload) {
      handleOnChangeFile(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, isNewUpload]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (fileNameUrl && fileNameUrl !== '') {
      validateFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNameUrl]);

  const updateValidation = (index: number, value: boolean) => {
    setValidations(prevValidations => {
      const newValidations = [...prevValidations];
      newValidations[index] = value;
      return newValidations;
    });
  };

  const renderValidations = (code: number | number[]) => {
    const errorCodes = Object.fromEntries(
      errorCodesMatrix.map((item, index) => [index + 1, item]),
    );
    const validationSteps = Object.keys(errorCodes).map(
      key => errorCodes[key as keyof typeof errorCodes],
    );

    let currentDelay = delay;

    for (let i = 0; i < validationSteps.length; i++) {
      const step = validationSteps[i];
      const isError = Array.isArray(code)
        ? step.some(s => code.includes(s))
        : step.includes(code);
      setTimeout(() => {
        updateValidation(i, !isError);
        if (!isError || continueOnError) {
          setPercent(((i + 1) / validationSteps.length) * 100);
          if (i === validationSteps.length - 1) {
            setVisitedValidationStep(true);
          }
        }
      }, currentDelay);

      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (isError && !continueOnError) break;

      currentDelay += delay;
    }
  };

  useEffect(() => {
    if (validations.some(value => value === false)) {
      setIsSuccessBar(false);
    }
  }, [validations]);

  const validateFile = async () => {
    try {
      if (!file) {
        setSnackbar('defaultError');
        return;
      }

      if (validationStepVisited && validationMetadata?.code) {
        return renderValidations(validationMetadata.code as number | number[]);
      }
      const filePath = centralized
        ? `${filePathUrl}/${fileNameUrl}`
        : fileNameUrl;

      const res = await onValidate(filePath);

      if (res) {
        const { success, response, errors, ...validationMetadata } = res;
        if (success) {
          renderValidations(0);
          if (centralized) {
            await onSubmit();
            handleNextStep();
          }

          onValidated({ ...validationMetadata });
        } else {
          if (response) {
            onValidated({ ...validationMetadata, code: response.code }, errors);
            renderValidations(response.code);
          } else {
            setSnackbar('defaultError');
          }
        }
        // biome-ignore lint/style/useBlockStatements: single-line early return
      } else setSnackbar('defaultError');
    } catch (err) {
      setSnackbar('defaultError');
    }
  };

  return {
    validations,
    percent: Number(percent.toFixed(0)),
    isSuccessBar,
  };
};

export default useDataValidation;
